import { apiClient } from '../client';
import type { Paginated, PaginationQuery } from './types';

export interface Project {
  id: number;
  parentId: number | null;
  name: string;
  projectNumber: string | null;
  customerId: number | null;
  primaryPersonId: number | null;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  businessLine: string | null;
  category: string | null;
  amount: string | null;
  currencyId: number | null;
  plannedHours: number | null;
  spentHours: number | null;
  assignedToId: number | null;
  countryId: number | null;
  region: string | null;
  website: string | null;
  email: string | null;
  archived: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  country?: { id: number; code: string; name: string } | null;
  currency?: { id: number; code: string; name?: string } | null;
  parent?: { id: number; name: string } | null;
  children?: Array<{ id: number; name: string; status: string | null }>;
}

export type ProjectListItem = Pick<
  Project,
  | 'id'
  | 'name'
  | 'projectNumber'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'amount'
  | 'archived'
  | 'currency'
>;

export interface ProjectListQuery extends PaginationQuery {
  customerId?: number;
  parentId?: number;
  assignedToId?: number;
  status?: string;
  archived?: boolean;
  keywordIds?: string;
}

export type ProjectInput = Partial<Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'version'>> & {
  name: string;
};

export const projectsApi = {
  async list(q: ProjectListQuery): Promise<Paginated<ProjectListItem>> {
    const { data } = await apiClient.get<Paginated<ProjectListItem>>('/projects', { params: q });
    return data;
  },
  async findOne(id: number): Promise<Project> {
    const { data } = await apiClient.get<Project>(`/projects/${id}`);
    return data;
  },
  async create(input: ProjectInput): Promise<Project> {
    const { data } = await apiClient.post<Project>('/projects', input);
    return data;
  },
  async update(id: number, input: Partial<ProjectInput>): Promise<Project> {
    const { data } = await apiClient.patch<Project>(`/projects/${id}`, input);
    return data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/projects/${id}`);
  },
};
